import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Printer, ArrowLeft } from "lucide-react";

const monthKey = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

export default function AdminLabels() {
  const [params, setParams] = useSearchParams();
  const scope = params.get("scope") || "coffrets";
  const month = params.get("month") || monthKey();
  const onlyPending = params.get("pending") !== "0";
  const [data, setData] = useState(null);

  useEffect(() => {
    const q = scope === "mailclub" && onlyPending ? `&month=${month}` : "";
    api.get(`/admin/labels?scope=${scope}${q}`).then(r => setData(r.data));
  }, [scope, month, onlyPending]);

  const labels = (data?.labels || []).filter(l => !l.missing);
  const missing = (data?.labels || []).filter(l => l.missing);
  const set = (k, v) => { const p = new URLSearchParams(params); p.set(k, v); setParams(p); };

  return (
    <div data-testid="admin-labels-page">
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #label-sheet, #label-sheet * { visibility: visible; }
          #label-sheet { position: absolute; left: 0; top: 0; width: 210mm; margin: 0; padding: 8mm 4mm; }
          @page { size: A4; margin: 0; }
        }
        #label-sheet { display: grid; grid-template-columns: repeat(2, 99mm); grid-auto-rows: 57mm; column-gap: 3mm; justify-content: center; }
        .label { padding: 8mm 10mm; font-family: Georgia, serif; font-size: 13pt; line-height: 1.35; color: #2C2621; break-inside: avoid; border: 1px dashed #E8DCC8; }
        @media print { .label { border: none; } }
      `}</style>

      <div className="print:hidden">
        <Link to={scope === "mailclub" ? "/admin/mailclub" : "/admin/coffrets"} className="text-xs text-[#7C7064] hover:text-[#A3876A] inline-flex items-center gap-1 mb-4"><ArrowLeft size={12}/> Retour</Link>
        <p className="font-hand text-xl text-[#A3876A] mb-1">Prêtes à coller</p>
        <h1 className="font-serif text-4xl mb-6">Étiquettes d'envoi</h1>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <select data-testid="labels-scope" value={scope} onChange={e => set("scope", e.target.value)} className="px-3 py-2 bg-white border border-[#E8DCC8] text-sm">
            <option value="coffrets">Coffrets à envoyer</option>
            <option value="mailclub">Enveloppes Club de Lettres</option>
          </select>
          {scope === "mailclub" && (
            <>
              <input data-testid="labels-month" type="month" value={month} onChange={e => set("month", e.target.value)} className="px-3 py-2 bg-white border border-[#E8DCC8] text-sm"/>
              <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={onlyPending} onChange={e => set("pending", e.target.checked ? "1" : "0")}/> Seulement celles pas encore envoyées ce mois</label>
            </>
          )}
          <button data-testid="labels-print" onClick={() => window.print()} className="btn-primary inline-flex items-center gap-2 ml-auto"><Printer size={14}/> Imprimer ({labels.length})</button>
        </div>
        <p className="text-xs text-[#7C7064] mb-4">Format : planche A4 de 10 étiquettes (2 × 5, 99 × 57 mm). Impression sans marges, échelle 100 %.</p>
        {missing.length > 0 && (
          <div data-testid="labels-missing" className="mb-6 p-3 border border-[#C9A18A] bg-[#FBEEEA] text-sm">
            <strong>{missing.length} adresse{missing.length > 1 ? "s" : ""} manquante{missing.length > 1 ? "s" : ""}</strong> : {missing.map(m => m.name || "?").join(", ")}
          </div>
        )}
        {data && labels.length === 0 && <p className="text-sm text-[#7C7064] italic">Aucune étiquette à imprimer.</p>}
      </div>

      <div id="label-sheet">
        {labels.map((l, i) => (
          <div key={i} className="label" data-testid={`label-${i}`}>
            <div style={{ fontWeight: 600 }}>{l.name}</div>
            <div>{l.line1}</div>
            {l.line2 && <div>{l.line2}</div>}
            <div>{l.postal_code} {l.city}</div>
            {l.country && !/^(fr|france)$/i.test(l.country) && <div>{l.country}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
